'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { MenuPublico } from '@/shared/components/layout/MenuPublico';

/**
 * Página 404 de AviFácil: Réplica exacta del diseño visual de referencia con MenuPublico,
 * título superior con humor en 2 líneas, Elie flotando con resplandor cálido y nube 404 verde bosque,
 * y botón de acción 'Volver a Casa 🐥'.
 */
export default function NotFound() {
  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: 'var(--bg-404, #EEF1F8)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Menú de navegación público superior oficial de AviFácil */}
      <MenuPublico />

      {/* Contenido principal centrado */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem 1.5rem 2.5rem',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'relative',
            maxWidth: '850px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* 1. Título superior en 2 líneas exactas */}
          <h1
            style={{
              fontFamily: "var(--font-nunito, 'Nunito', -apple-system, BlinkMacSystemFont, sans-serif)",
              fontSize: '1.55rem',
              fontWeight: 800,
              color: '#152520',
              textAlign: 'center',
              lineHeight: 1.35,
              marginBottom: '1.25rem',
              maxWidth: '750px',
              width: '100%',
            }}
          >
            <span style={{ display: 'block' }}>
              ¡Ayy! Elie buscó en todos los nidos... ¡pero esta
            </span>
            <span style={{ display: 'block' }}>
              página voló!
            </span>
          </h1>

          {/* 2. Contenedor de Elie meditando, resplandor, nube y sombra */}
          <div
            style={{
              position: 'relative',
              maxWidth: '380px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Personaje con animación de flotación vertical suave y aura de iluminación */}
            <motion.div
              animate={{
                y: [-12, 12, -12],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              style={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
              }}
            >
              {/* Resplandor áurico de iluminación cálida detrás de Elie */}
              <motion.div
                animate={{
                  scale: [0.95, 1.15, 0.95],
                  opacity: [0.75, 1, 0.75],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  position: 'absolute',
                  width: '320px',
                  height: '320px',
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(250, 204, 21, 0.58) 0%, rgba(255, 237, 109, 0.35) 45%, rgba(178, 232, 116, 0.18) 64%, transparent 80%)',
                  filter: 'blur(32px)',
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />

              <Image
                src="/material%20grafico%20404/Elie_meditando.png"
                alt="AviFácil 404 - Elie meditando"
                width={360}
                height={360}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  userSelect: 'none',
                  pointerEvents: 'none',
                  position: 'relative',
                  zIndex: 2,
                }}
                priority
              />

              {/* Nube de pensamiento animada con borde verde bosque, 404 y 2 burbujitas hacia Elie */}
              <motion.div
                animate={{
                  y: [-6, 6, -6],
                }}
                transition={{
                  duration: 3.6,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '18px',
                  zIndex: 10,
                  pointerEvents: 'none',
                  filter: 'drop-shadow(0 8px 18px rgba(10, 52, 39, 0.16))',
                }}
              >
                <svg
                  width="145"
                  height="102"
                  viewBox="0 0 146 102"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ overflow: 'visible', display: 'block' }}
                >
                  {/* Silueta esponjosa de nube con contorno verde bosque */}
                  <path
                    d="M 40 68 A 17 17 0 0 1 20 50 A 16 16 0 0 1 24 30 A 20 20 0 0 1 52 14 A 25 25 0 0 1 92 14 A 20 20 0 0 1 122 30 A 18 18 0 0 1 126 54 A 18 18 0 0 1 104 72 A 22 22 0 0 1 66 75 A 18 18 0 0 1 40 68 Z"
                    fill="#ffffff"
                    stroke="#0A3427"
                    strokeWidth="2.4"
                    strokeLinejoin="round"
                  />

                  {/* Burbujitas conectoras de pensamiento hacia la cabeza de Elie */}
                  <circle
                    cx="26"
                    cy="81"
                    r="6"
                    fill="#ffffff"
                    stroke="#0A3427"
                    strokeWidth="2"
                  />
                  <circle
                    cx="15"
                    cy="92"
                    r="4"
                    fill="#ffffff"
                    stroke="#0A3427"
                    strokeWidth="1.8"
                  />

                  {/* Número 404 en verde bosque profundo */}
                  <text
                    x="73"
                    y="44"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#0A3427"
                    fontFamily="var(--font-nunito, 'Nunito', sans-serif)"
                    fontSize="27"
                    fontWeight="900"
                    letterSpacing="0.02em"
                  >
                    404
                  </text>
                </svg>
              </motion.div>
            </motion.div>

            {/* Sombra elíptica debajo de Elie sincronizada con la flotación */}
            <motion.div
              animate={{
                scale: [1, 0.74, 1],
                opacity: [0.8, 0.45, 0.8],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              style={{
                width: '240px',
                height: '24px',
                borderRadius: '50%',
                background:
                  'radial-gradient(ellipse at center, rgba(14, 53, 40, 0.52) 0%, rgba(14, 53, 40, 0.22) 55%, transparent 78%)',
                filter: 'blur(5px)',
                marginTop: '-6px',
                zIndex: 1,
              }}
            />
          </div>

          {/* 3. Botón de acción 'Volver a Casa 🐥' con estilo píldora verde bosque */}
          <motion.div
            style={{ marginTop: '2.25rem', zIndex: 10 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            <Link
              href="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.65rem',
                backgroundColor: '#0A3427',
                color: '#ffffff',
                fontFamily: "var(--font-nunito, 'Nunito', -apple-system, BlinkMacSystemFont, sans-serif)",
                fontSize: '0.95rem',
                fontWeight: 700,
                padding: '0.75rem 1.65rem',
                borderRadius: '9999px',
                textDecoration: 'none',
                boxShadow: '0 6px 18px -2px rgba(10, 52, 39, 0.35)',
                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#0e4534';
                e.currentTarget.style.boxShadow = '0 8px 24px -2px rgba(10, 52, 39, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#0A3427';
                e.currentTarget.style.boxShadow = '0 6px 18px -2px rgba(10, 52, 39, 0.35)';
              }}
            >
              {/* Icono flecha izquierda */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>

              <span>Volver a Casa</span>

              {/* Emoji / icono pollito */}
              <span style={{ fontSize: '1.15rem', lineHeight: 1 }}>🐥</span>
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
